import { KEYBINDS } from '../../config/constants.js';

/**
 * Polls raw keyboard state and exposes it as gameplay-friendly queries.
 * Systems ask "what does the player want to do" (getMoveVector, consumeDash)
 * rather than reading key codes directly — keeps rebinding/gamepad support
 * a one-file change later.
 */
export class InputManager {
  /**
   * @param {EventTarget} target
   * @param {object} [opts]
   * @param {{ toggleHold: boolean }} [opts.save] - read live, so flipping the
   *   setting mid-run takes effect immediately rather than on the next run.
   */
  constructor(target = window, { save = null } = {}) {
    this._save = save;
    this._down = new Set();
    // Latched state for the accessibility "toggle instead of hold" mode
    // (save.toggleHold). Focus and dash are the two sustained holds in the
    // game, and focus in particular is held for a whole pattern — which is
    // precisely the input shape that hurts under limited hand mobility.
    // Each keypress flips the latch; the getters below OR it with the raw
    // held state so the two modes never fight.
    this._focusLatched = false;
    this._dashLatched = false;
    // Retained for compatibility with callers that consume one-shot input;
    // gameplay uses isDashHeld() so holding Shift auto-dashes when ready.
    this._dashQueued = false;
    // Edge-triggered pause toggle, same reasoning as dash.
    this._pauseQueued = false;
    // Edge-triggered champion ultimate (Space, or Q/E), same reasoning as dash.
    this._ultimateQueued = false;
    // Edge-triggered stance swap (Act III's Fugue), same reasoning as dash.
    this._stanceSwapQueued = false;

    // Touch equivalents (see ui/TouchControls.js) — kept as separate state
    // rather than synthesizing KeyboardEvents, since the virtual joystick is
    // analog (not a fixed 8-way key set) and buttons need to work even on
    // devices with no `target` keyboard listener at all. Merged with
    // keyboard state in the getters/consumers below so both input sources
    // work simultaneously (e.g. a touch device with a paired keyboard).
    this._touchMoveX = 0;
    this._touchMoveY = 0;
    this._touchDashHeld = false;
    this._touchFocusHeld = false;

    this._onKeyDown = (e) => {
      if (!this._down.has(e.code)) {
        if (this._toggleHold) {
          if (KEYBINDS.focus.includes(e.code)) this._focusLatched = !this._focusLatched;
          if (KEYBINDS.dash.includes(e.code)) this._dashLatched = !this._dashLatched;
        }
        if (KEYBINDS.dash.includes(e.code)) this._dashQueued = true;
        if (KEYBINDS.pause.includes(e.code)) this._pauseQueued = true;
        if (KEYBINDS.ultimate.includes(e.code)) this._ultimateQueued = true;
        if (KEYBINDS.stanceSwap.includes(e.code)) this._stanceSwapQueued = true;
      }
      this._down.add(e.code);
    };
    this._onKeyUp = (e) => this._down.delete(e.code);
    // Lose focus (alt-tab, etc.) — drop all held keys so movement doesn't
    // stick. The latches drop too: coming back to a window that is silently
    // already focusing or auto-dashing is the same "stuck input" surprise.
    this._onBlur = () => {
      this._down.clear();
      this.clearHoldLatches();
    };

    target.addEventListener('keydown', this._onKeyDown);
    target.addEventListener('keyup', this._onKeyUp);
    window.addEventListener('blur', this._onBlur);
    this._target = target;
  }

  _anyDown(codes) {
    return codes.some((c) => this._down.has(c));
  }

  get _toggleHold() {
    return this._save?.toggleHold === true;
  }

  /**
   * Drops both latches. Called on blur and by Game when a run ends or pauses,
   * so a latch set during one run can't leak into the next.
   */
  clearHoldLatches() {
    this._focusLatched = false;
    this._dashLatched = false;
  }

  /** Normalized movement vector from WASD/arrows + the virtual joystick, {x, y} each in [-1, 1]. */
  getMoveVector() {
    let x = 0;
    let y = 0;
    if (this._anyDown(KEYBINDS.left)) x -= 1;
    if (this._anyDown(KEYBINDS.right)) x += 1;
    if (this._anyDown(KEYBINDS.up)) y -= 1;
    if (this._anyDown(KEYBINDS.down)) y += 1;

    if (x !== 0 && y !== 0) {
      const inv = 1 / Math.sqrt(2);
      x *= inv;
      y *= inv;
    }

    x += this._touchMoveX;
    y += this._touchMoveY;
    const mag = Math.hypot(x, y);
    if (mag > 1) {
      x /= mag;
      y /= mag;
    }
    return { x, y };
  }

  /** Called by TouchControls as the joystick knob moves; each axis already in [-1, 1]. */
  setTouchMoveVector(x, y) {
    this._touchMoveX = x;
    this._touchMoveY = y;
  }

  /** True once on the tick after dash was pressed; consumes the press. */
  consumeDashPressed() {
    if (this._dashQueued) {
      this._dashQueued = false;
      return true;
    }
    return false;
  }

  /**
   * True while any configured dash key is held, or the touch dash button is
   * held, or (under save.toggleHold) the dash latch is set.
   *
   * Worth being explicit about what latched dash MEANS: holding dash already
   * auto-dashes whenever a charge is available, so latching it is a
   * continuous auto-dash for as long as it stays on. That is a real gameplay
   * effect, not just an input convenience — it is opt-in and the settings
   * copy says so.
   */
  isDashHeld() {
    return this._anyDown(KEYBINDS.dash) || this._touchDashHeld || (this._toggleHold && this._dashLatched);
  }

  /** Called by TouchControls on dash-button pointerdown/pointerup. */
  setTouchDashHeld(held) {
    // Under toggleHold the touch button is a tap, not a hold: latch on press
    // and ignore the release, so the on-screen button matches the key.
    if (this._toggleHold) {
      if (held) this._dashLatched = !this._dashLatched;
      return;
    }
    this._touchDashHeld = held;
  }

  /** True while any configured focus key is held, or the touch focus button is held (§D4), or the focus latch is set. */
  isFocusHeld() {
    return this._anyDown(KEYBINDS.focus) || this._touchFocusHeld || (this._toggleHold && this._focusLatched);
  }

  /** Called by TouchControls on focus-button pointerdown/pointerup. */
  setTouchFocusHeld(held) {
    if (this._toggleHold) {
      if (held) this._focusLatched = !this._focusLatched;
      return;
    }
    this._touchFocusHeld = held;
  }

  /** True once on the frame after the pause key was pressed; consumes the press. */
  consumePausePressed() {
    if (this._pauseQueued) {
      this._pauseQueued = false;
      return true;
    }
    return false;
  }

  /** Called by TouchControls on pause-button tap — edge-triggered, same as the keyboard binding. */
  queuePausePressed() {
    this._pauseQueued = true;
  }

  /** True once on the tick after the ultimate key was pressed; consumes the press. */
  consumeUltimatePressed() {
    if (this._ultimateQueued) {
      this._ultimateQueued = false;
      return true;
    }
    return false;
  }

  /** Called by TouchControls on ultimate-button tap — edge-triggered, same as the keyboard binding. */
  queueUltimatePressed() {
    this._ultimateQueued = true;
  }

  /** True once on the tick after the stance-swap key was pressed; consumes the press. */
  consumeStanceSwapPressed() {
    if (this._stanceSwapQueued) {
      this._stanceSwapQueued = false;
      return true;
    }
    return false;
  }

  /** Called by TouchControls on the stance button — edge-triggered, same as the keyboard binding. */
  queueStanceSwapPressed() {
    this._stanceSwapQueued = true;
  }

  destroy() {
    this._target.removeEventListener('keydown', this._onKeyDown);
    this._target.removeEventListener('keyup', this._onKeyUp);
    window.removeEventListener('blur', this._onBlur);
  }
}
